import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Input } from 'antd';
import HFbutton from '../../button/HFbutton';
import TextWinds from '../../common/TextWinds';
import { Field } from '../../common/form/Field';
import { ClassGroupDescriptionSection } from './ClassGroupDescriptionSection';
import { LmsClassGroupType } from '../../../constants/types/lms-type';
import { createClassGroup } from '../../../api/utils/lms/class-group/api';
import { getErrorMessages } from '../../../api/utils/util-func';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import {
  CLASS_GROUP_DESCRIPTION_MAX_LENGTH,
  CLASS_GROUP_DESCRIPTION_PLACEHOLDER,
  CLASS_GROUP_NAME_MAX_LENGTH,
  CLASS_GROUP_NAME_PLACEHOLDER,
  classGroupDescriptionRules,
  classGroupNameRules,
} from '../../../constants/validation';
import {
  DUPLICATED_CLASS_GROUP_NAME_MESSAGE,
  isDuplicatedClassGroupName,
} from './isDuplicatedClassGroupName';

type AddClassGroupForm = { name: string; description: string };

interface ClassGroupAddCardProps {
  classId: string;
  /** 이름 중복 확인에 쓸 전체 반 목록 */
  classGroups: LmsClassGroupType[] | undefined;
  reloadClassGroups: () => void;
}

/** 반 관리 > 새로운 반 추가 카드 */
const ClassGroupAddCard = ({
  classId,
  classGroups,
  reloadClassGroups,
}: ClassGroupAddCardProps) => {
  const dispatch = useAppDispatch();
  const [isAdding, setIsAdding] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AddClassGroupForm>({
    defaultValues: { name: '', description: '' },
  });

  const addHandler = async ({ name, description }: AddClassGroupForm) => {
    const trimmed = name.trim();

    // 같은 이름의 반이 있으면 목록에서 어느 쪽인지 구분할 수 없으므로 막는다.
    if (isDuplicatedClassGroupName(trimmed, classGroups)) {
      setError(
        'name',
        { message: DUPLICATED_CLASS_GROUP_NAME_MESSAGE },
        { shouldFocus: true },
      );
      return;
    }

    setIsAdding(true);
    try {
      await createClassGroup(classId, {
        name: trimmed,
        description: description.trim(),
      });
      reset();
      reloadClassGroups();
      dispatch(openAlert({ message: '반을 추가했습니다.' }));
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="flex flex-col bg-white xl:w-[420px] xl:shrink-0 xl:rounded-lg xl:shadow-card">
      <div className="px-4 py-3 xl:rounded-t-lg xl:px-6 xl:py-4">
        <TextWinds type="title_h3" color="gray9">
          새로운 반 추가
        </TextWinds>
      </div>

      <form onSubmit={handleSubmit(addHandler)} className="p-4 xl:p-6">
        <div className="flex flex-col">
          <Field label="반 이름" hint="필수" error={errors.name?.message}>
            <Controller
              control={control}
              name="name"
              rules={classGroupNameRules({ required: true })}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  maxLength={CLASS_GROUP_NAME_MAX_LENGTH}
                  showCount
                  placeholder={CLASS_GROUP_NAME_PLACEHOLDER}
                  status={errors.name ? 'error' : undefined}
                  className="rounded p-3"
                  autoComplete="off"
                />
              )}
            />
          </Field>

          <Field label="반 설명" error={errors.description?.message}>
            <Controller
              control={control}
              name="description"
              rules={classGroupDescriptionRules()}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  rows={3}
                  maxLength={CLASS_GROUP_DESCRIPTION_MAX_LENGTH}
                  showCount
                  placeholder={CLASS_GROUP_DESCRIPTION_PLACEHOLDER}
                  status={errors.description ? 'error' : undefined}
                  className="rounded p-3"
                  autoComplete="off"
                />
              )}
            />
          </Field>

          {/* 이름과 설명을 모두 입력한 뒤 누르도록 버튼을 필드 아래로 내린다. */}
          <div className="mt-2 flex justify-end">
            <HFbutton
              width="auto"
              height={40}
              onClick={handleSubmit(addHandler)}
              disabled={isAdding}
            >
              추가하기
            </HFbutton>
          </div>
        </div>
      </form>

      {/* 반 기능 안내: 반을 만드는 자리 바로 아래에 두어 맥락을 맞추고, 페이지 헤더 공간을 아낀다. */}
      <div className="px-4 pb-4 xl:px-6 xl:pb-6">
        <ClassGroupDescriptionSection />
      </div>
    </div>
  );
};

export default ClassGroupAddCard;
